import React, { useState, useEffect } from 'react';
import API from '../../api';
import toastr from 'toastr';
import { useAuth } from '../../contexts/AuthContext';
import 'toastr/build/toastr.min.css';

interface SettingsTabProps {
  isActive: boolean;
}

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  address: string;
}

const SettingsTab = ({ isActive }: SettingsTabProps) => {
  const { user, setUser, fetchCurrentUser } = useAuth() as any;

  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    address: '',
  });

  const [isSaving, setIsSaving] = useState(false);

  // Prefill user data
  useEffect(() => {
    if (user) {
      setFormData({
        fullName: user.full_name ?? user.fullName ?? '',
        email: user.email ?? '',
        phone: user.phone ?? '',
        address: user.address ?? '',
      });
    }
  }, [user]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const payload = {
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
      };

      const response = await API.post('/updateProfile', payload);
      const respData = response?.data ?? response;
      const updatedUser = respData?.user ?? respData?.data ?? respData ?? null;

      // ✅ Toastr config
      toastr.options = {
        closeButton: true,
        progressBar: true,
        positionClass: 'toast-top-right',
        timeOut: 3000,
      };
      toastr.success('Profile updated successfully 🎉', 'Success');

      // ✅ Update context immediately
      if (setUser) {
        const newUser = {
          ...user,
          full_name: payload.full_name,
          email: payload.email,
          phone: payload.phone,
          address: payload.address,
          ...(typeof updatedUser === 'object' ? updatedUser : {}),
        };
        setUser(newUser);

        // ✅ Also update local formData immediately
        setFormData({
          fullName: newUser.full_name ?? '',
          email: newUser.email ?? '',
          phone: newUser.phone ?? '',
          address: newUser.address ?? '',
        });
      }

      // ✅ Refetch user (optional, to keep backend sync)
      if (typeof fetchCurrentUser === 'function') {
        try {
          await fetchCurrentUser();
        } catch (fetchErr) {
          console.warn('Failed to refetch user after save', fetchErr);
        }
      }
    } catch (err: any) {
      console.error('Error updating profile:', err);
      const message =
        err?.response?.data?.message ??
        err?.message ??
        'Failed to update profile';
      toastr.error(message, 'Error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={`tab-pane ${isActive ? 'active' : ''}`}>
      <form className="form-horizontal" onSubmit={handleSubmit}>
        {/* Full Name */}
        <div className="form-group row">
          <label htmlFor="fullName" className="col-sm-2 col-form-label">
            Name
          </label>
          <div className="col-sm-10">
            <input
              type="text"
              className="form-control"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Enter your full name"
              required
            />
          </div>
        </div>

        {/* Email */}
        <div className="form-group row">
          <label htmlFor="email" className="col-sm-2 col-form-label">
            Email
          </label>
          <div className="col-sm-10">
            <input
              type="email"
              className="form-control"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              required
            />
          </div>
        </div>

        {/* Phone */}
        <div className="form-group row">
          <label htmlFor="phone" className="col-sm-2 col-form-label">
            Phone
          </label>
          <div className="col-sm-10">
            <input
              type="tel"
              className="form-control"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter your phone number"
            />
          </div>
        </div>

        {/* Address */}
        <div className="form-group row">
          <label htmlFor="address" className="col-sm-2 col-form-label">
            Address
          </label>
          <div className="col-sm-10">
            <textarea
              className="form-control"
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="Enter your address"
              rows={3}
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="form-group row">
          <div className="offset-sm-2 col-sm-10">
            <button className="btn btn-primary" type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SettingsTab;
